import { SceneService } from '../../services/SceneService';
import { Element } from '../models/Element';
import { LayoutSegmentFactory, Scene } from '../models/layout';
import { computeCoverCrop } from '../models/layout/CropTransformUtils';
import { mapFitTypeToTemplateId } from '../models/layout/legacyLayoutMapping';
import { EditorChangeType } from '../types';
import { AbstractCommand } from './AbstractCommand';

import type { NaturalDimensions } from '../types/aspectRatio';
import type { SceneDetection } from '../types/sceneDetection';
import type { VideoEditor } from '../VideoEditor';

/**
 * Scene segment that overlaps with an element
 */
interface SceneSegment {
  sceneIndex: number;
  startTime: number;
  endTime: number;
}

/**
 * Element with associated overlapping scenes
 */
interface ElementWithScenes {
  element: Element;
  elementIndex: number;
  mediaStartTime: number;
  mediaEndTime: number;
  overlappingScenes: SceneSegment[];
}

/**
 * CreateScenesByMediaTimeCommand
 * Algorithm:
 * 1. For each scene detection, find all elements that overlap with it
 * 2. Build a map of elements with their overlapping scene segments
 * 3. Iterate over elements, creating scenes anchored to them
 * 4. A scene can continue to next element if it's the last scene of current element
 *    and first scene of next element (gap allowed, but media time order preserved)
 * 5. Same scene can appear multiple times if elements are re-ordered
 * Replaces all existing scenes on the timeline
 */
export class CreateScenesByMediaTimeCommand extends AbstractCommand {
  private sceneDetections: SceneDetection[];
  private newScenes: Scene[] = [];
  private previousScenes: Scene[] = [];
  private service: SceneService;
  private readonly naturalMediaDimensions:
    | Record<string, NaturalDimensions>
    | undefined;

  constructor(
    editor: VideoEditor,
    sceneDetections: SceneDetection[],
    naturalMediaDimensions?: Record<string, NaturalDimensions>,
  ) {
    super(editor);
    this.sceneDetections = sceneDetections;
    this.naturalMediaDimensions = naturalMediaDimensions;
    this.service = new SceneService();
  }

  getName(): string {
    return 'CreateScenesByMediaTimeCommand';
  }

  execute(): boolean {
    try {
      // Store previous scenes for undo
      this.previousScenes = [...this.editor.timeline.scenes];

      // Get the video transcript track
      const transcriptTrack = this.editor.getVideoTranscriptTrack();
      if (!transcriptTrack) {
        console.warn('No video transcript track found');
        return false;
      }

      const elements = [...transcriptTrack.elements];
      if (elements.length === 0) {
        console.warn('No elements on video transcript track');
        return false;
      }

      // Step 1: Build map of elements with their overlapping scenes
      const elementsWithScenes: ElementWithScenes[] = elements.map(
        (element, elementIndex) => {
          const mediaStartTime = this.service.getElementMediaStartTime(element);
          const mediaEndTime = this.service.getElementMediaEndTime(element);
          const overlappingScenes: SceneSegment[] = [];

          // Find all scenes that overlap with this element
          this.sceneDetections.forEach((detection, sceneIndex) => {
            const sceneStartTime = detection.startTime;
            const sceneEndTime = detection.startTime + detection.lengthSeconds;

            // Check if scene overlaps with element
            if (
              sceneEndTime > mediaStartTime &&
              sceneStartTime < mediaEndTime
            ) {
              overlappingScenes.push({
                sceneIndex,
                startTime: sceneStartTime,
                endTime: sceneEndTime,
              });
            }
          });

          return {
            element,
            elementIndex,
            mediaStartTime,
            mediaEndTime,
            overlappingScenes,
          };
        },
      );

      // Filter out elements with no overlapping scenes
      const relevantElements = elementsWithScenes.filter(
        ew => ew.overlappingScenes.length > 0,
      );

      if (relevantElements.length === 0) {
        console.warn('No elements overlap with scene detections');
        return false;
      }

      // Step 2: Iterate over elements and create scenes
      this.newScenes = [];
      const templateId = mapFitTypeToTemplateId('cover');

      // Track which element+scene combinations we've processed as start points
      const processedStarts = new Set<string>();

      for (let i = 0; i < relevantElements.length; i++) {
        const currentElementData = relevantElements[i];
        const currentScenes = currentElementData.overlappingScenes;

        // Process each scene in this element
        for (let sceneIdx = 0; sceneIdx < currentScenes.length; sceneIdx++) {
          const currentScene = currentScenes[sceneIdx];

          // Create unique key for this element+scene+sceneIdx combination
          const startKey = `${currentElementData.elementIndex}_${currentScene.sceneIndex}_${sceneIdx}`;

          // Skip if we already processed this exact start point
          if (processedStarts.has(startKey)) {
            continue;
          }

          // Check if this is the last scene of current element
          const isLastSceneOfElement = sceneIdx === currentScenes.length - 1;

          // Find where this scene ends
          let endElementIndex = i;
          let endScene = currentScene;

          // If this is the last scene of current element, check if it continues to next elements
          if (isLastSceneOfElement) {
            for (let j = i + 1; j < relevantElements.length; j++) {
              const nextElementData = relevantElements[j];
              const nextScenes = nextElementData.overlappingScenes;

              // Check if next element has scenes
              if (nextScenes.length === 0) break;

              // Check if first scene of next element matches current scene
              const nextFirstScene = nextScenes[0];
              const isSameScene =
                nextFirstScene.sceneIndex === currentScene.sceneIndex;

              // Check if media time order is preserved
              const mediaTimeOrderPreserved =
                nextElementData.mediaStartTime >=
                currentElementData.mediaStartTime;

              if (isSameScene && mediaTimeOrderPreserved) {
                // Scene continues to next element
                endElementIndex = j;
                endScene = nextFirstScene;

                // If this is also the last scene of next element, continue checking
                const isLastOfNext = nextScenes.length === 1;
                if (!isLastOfNext) {
                  break;
                }
              } else {
                // Scene doesn't continue
                break;
              }
            }
          }

          // Create anchor start
          // Clamp scene start time to element's media time range
          const startElementData = currentElementData;
          const clampedStartTime = Math.max(
            currentScene.startTime,
            startElementData.mediaStartTime,
          );
          const startAnchor = this.service.createAnchorForMediaTime(
            startElementData.element,
            clampedStartTime,
            startElementData.mediaStartTime,
            startElementData.mediaEndTime,
          );

          // Create anchor end
          // Clamp scene end time to element's media time range
          const endElementData = relevantElements[endElementIndex];
          const clampedEndTime = Math.min(
            endScene.endTime,
            endElementData.mediaEndTime,
          );
          const endAnchor = this.service.createAnchorForMediaTime(
            endElementData.element,
            clampedEndTime,
            endElementData.mediaStartTime,
            endElementData.mediaEndTime,
          );

          if (!startAnchor || !endAnchor) {
            console.warn(
              `Could not create anchors for scene ${currentScene.sceneIndex}`,
            );
            continue;
          }

          // Compute cover crop from the scene's start element if dimensions are available
          const crop = this.computeCoverCropForElement(
            startElementData.element,
          );

          const layout = LayoutSegmentFactory.create(
            templateId,
            transcriptTrack.id,
            crop ?? undefined,
          );

          const scene = new Scene({
            anchorStart: startAnchor,
            anchorEnd: endAnchor,
            layout,
          });

          this.newScenes.push(scene);
          processedStarts.add(startKey);
        }
      }

      if (this.newScenes.length === 0) {
        return false;
      }

      // Replace all scenes with the new ones
      this.service.replaceScenes(this.editor.timeline, this.newScenes);

      // Validate and adjust scenes (close gaps, remove too short scenes)
      this.service.validateAndAdjustScenes(this.editor.timeline);

      this.editor.markDirty();

      // Notify that scenes were replaced
      this.editor.notifyObservers(EditorChangeType.SCENE_ADDED, {
        sceneId: 'multiple',
        count: this.newScenes.length,
      });

      return true;
    } catch (error) {
      console.error('Failed to create scenes by media time:', error);
      return false;
    }
  }

  undo(): boolean {
    try {
      // Restore previous scenes
      this.service.replaceScenes(this.editor.timeline, this.previousScenes);

      this.editor.markDirty();

      // Notify that scenes were restored
      this.editor.notifyObservers(EditorChangeType.SCENE_REMOVED, {
        sceneId: 'multiple',
        count: this.newScenes.length,
      });

      return true;
    } catch (error) {
      console.error('Failed to undo scene creation:', error);
      return false;
    }
  }

  redo(): boolean {
    return this.execute();
  }

  /**
   * Computes a cover crop for a video element.
   * Returns a centered crop that fills the canvas while maintaining aspect ratio,
   * or null if dimensions are unavailable.
   */
  private computeCoverCropForElement(
    element: Element,
  ): { x: number; y: number; w: number; h: number } | null {
    if (!this.naturalMediaDimensions) {
      return null;
    }

    if (!Element.isVideoElement(element)) {
      return null;
    }

    const sourceUrl = element.source;
    const naturalDimensions = this.naturalMediaDimensions[sourceUrl];

    if (!naturalDimensions) {
      return null;
    }

    return computeCoverCrop(
      this.editor.timeline.width,
      this.editor.timeline.height,
      naturalDimensions.width,
      naturalDimensions.height,
    );
  }

  getDescription(): string {
    return `Create ${this.sceneDetections.length} scene(s) by media time`;
  }

  shouldCreateUndoRedoEntry(): boolean {
    return true;
  }
}
